/**
 * NavigatorX Notification Center
 * 
 * Component for sending communications to users:
 * - Send email (via Cloud Function)
 * - Send SMS (via Twilio Cloud Function)
 * - Send push notification (via FCM)
 * - In-app notification
 * - Notification templates
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  X,
  Mail,
  Phone,
  Bell,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Smartphone,
  FileText,
  ChevronDown,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../../utils/firebase/client';
import { sendUserNotification, sendPushNotification } from '../../../utils/services/navigatorXService';
import { logAdminAction } from '../../../utils/services/navigatorXAuditService';
import type { NavUser } from '../../../types/navigatorx';

interface NotificationCenterProps {
  user: NavUser;
  onClose: () => void;
  onSent?: () => void;
}

type NotificationType = 'email' | 'sms' | 'push' | 'in_app';

// Pre-defined templates
const TEMPLATES = {
  email: [
    {
      id: 'welcome',
      name: 'Welcome Email',
      subject: 'Welcome to NavigatorX!',
      body: `Hi {{firstName}},

Welcome to NavigatorX! We're excited to have you on board.

Your trial period has started and you have full access to all premium features including:
- Voice-activated well search
- Offline maps
- Hazard reporting
- Referral rewards

If you have any questions, feel free to reach out.

Happy navigating!
The NavigatorX Team`
    },
    {
      id: 'trial_ending',
      name: 'Trial Ending Soon',
      subject: 'Your NavigatorX trial ends soon',
      body: `Hi {{firstName}},

Your NavigatorX trial is ending in 3 days. Don't lose access to:
- Voice-activated search
- Offline maps
- Real-time hazard alerts

Subscribe now at just $9.99/month to keep your premium features.

Questions? Reply to this email.

The NavigatorX Team`
    },
    {
      id: 'referral_success',
      name: 'Referral Success',
      subject: 'You earned a free month! 🎉',
      body: `Hi {{firstName}},

Great news! Your referral just completed their first paid month, which means you've earned a FREE month of NavigatorX!

Your free month will be automatically applied to your next billing cycle.

Keep referring friends with your code: {{referralCode}}

Thanks for spreading the word!
The NavigatorX Team`
    }
  ],
  sms: [
    {
      id: 'welcome_sms',
      name: 'Welcome SMS',
      body: 'Welcome to NavigatorX! Your trial has started. Download the app and start navigating with voice commands today.'
    },
    {
      id: 'trial_ending_sms',
      name: 'Trial Ending',
      body: 'Your NavigatorX trial ends in 3 days. Subscribe now at $9.99/mo to keep premium features. Reply STOP to opt out.'
    },
    {
      id: 'referral_sms',
      name: 'Referral Earned',
      body: 'You earned a FREE month of NavigatorX! Your referral just subscribed. Keep sharing your code: {{referralCode}}'
    }
  ],
  push: [
    {
      id: 'welcome_push',
      name: 'Welcome',
      title: 'Welcome to NavigatorX! 🛢️',
      body: 'Your trial has started. Try saying "Hey Navigator, find the nearest well"'
    },
    {
      id: 'trial_ending_push',
      name: 'Trial Ending',
      title: 'Trial ending soon ⏰',
      body: 'Your trial ends in 3 days. Subscribe to keep premium features.'
    },
    {
      id: 'new_feature_push',
      name: 'New Feature',
      title: 'New Feature Available! 🎉',
      body: 'Check out the latest NavigatorX update with improved voice commands.'
    }
  ],
  in_app: [
    {
      id: 'welcome_inapp',
      name: 'Welcome',
      title: 'Welcome to NavigatorX!',
      body: 'Thanks for joining. Explore the app and discover all premium features.',
      type: 'info'
    },
    {
      id: 'referral_inapp',
      name: 'Referral Credit',
      title: 'You earned a free month!',
      body: 'Your referral subscribed. Free month will be applied automatically.',
      type: 'success'
    },
    {
      id: 'subscription_inapp',
      name: 'Subscription Active',
      title: 'Subscription Confirmed ✓',
      body: 'Your NavigatorX subscription is now active. Enjoy all premium features!',
      type: 'success'
    }
  ]
};

export default function NotificationCenter({ user, onClose, onSent }: NotificationCenterProps) {
  const [activeType, setActiveType] = useState<NotificationType>('push');
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  
  // Form state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [inAppTitle, setInAppTitle] = useState('');
  const [inAppBody, setInAppBody] = useState('');
  const [inAppType, setInAppType] = useState<'info' | 'success' | 'warning'>('info');

  const adminId = auth.currentUser?.uid || 'unknown';

  // Replace template variables
  const replaceVariables = (text: string) => {
    return text
      .replace(/{{firstName}}/g, user.firstName || 'there')
      .replace(/{{lastName}}/g, user.lastName || '')
      .replace(/{{displayName}}/g, user.displayName || 'User')
      .replace(/{{email}}/g, user.email || '')
      .replace(/{{referralCode}}/g, user.referralCode || 'XXXXXX');
  };

  // Load template
  const loadTemplate = (templateId: string, type: NotificationType) => {
    const templates = TEMPLATES[type] as any[];
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setSelectedTemplate(templateId);

    switch (type) {
      case 'email':
        setEmailSubject(replaceVariables(template.subject));
        setEmailBody(replaceVariables(template.body));
        break;
      case 'sms':
        setSmsBody(replaceVariables(template.body));
        break;
      case 'push':
        setPushTitle(replaceVariables(template.title));
        setPushBody(replaceVariables(template.body));
        break;
      case 'in_app':
        setInAppTitle(replaceVariables(template.title));
        setInAppBody(replaceVariables(template.body));
        setInAppType(template.type || 'info');
        break;
    }
  };

  // Send Email
  const handleSendEmail = async () => {
    if (!user.email) {
      toast.error('User has no email address');
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please enter subject and body');
      return;
    }

    setSending(true);
    try {
      const sendEmail = httpsCallable(functions, 'sendNavigatorEmail');
      await sendEmail({
        to: user.email,
        subject: emailSubject,
        body: emailBody,
        userId: user.id
      });

      await logAdminAction(adminId, 'SEND_EMAIL', user.id, {
        to: user.email,
        subject: emailSubject
      });

      toast.success(`Email sent to ${user.email}`);
      onSent?.();
      onClose();
    } catch (error: any) {
      console.error('Send email error:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Send SMS
  const handleSendSMS = async () => {
    if (!user.phone) {
      toast.error('User has no phone number');
      return;
    }
    if (!smsBody.trim()) {
      toast.error('Please enter message');
      return;
    }
    if (smsBody.length > 160) {
      toast.error('SMS must be 160 characters or less');
      return;
    }

    setSending(true);
    try {
      const sendSMS = httpsCallable(functions, 'sendNavigatorSMS');
      await sendSMS({
        to: user.phone,
        body: smsBody,
        userId: user.id
      });

      await logAdminAction(adminId, 'SEND_SMS', user.id, {
        to: user.phone,
        bodyLength: smsBody.length
      });

      toast.success(`SMS sent to ${user.phone}`);
      onSent?.();
      onClose();
    } catch (error: any) {
      console.error('Send SMS error:', error);
      toast.error(error.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  // Send Push Notification
  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) {
      toast.error('Please enter title and body');
      return;
    }

    setSending(true);
    try {
      await sendPushNotification(user.id, pushTitle, pushBody, {}, adminId);
      toast.success('Push notification sent');
      onSent?.();
      onClose();
    } catch (error: any) {
      console.error('Send push error:', error);
      toast.error(error.message || 'Failed to send push notification');
    } finally {
      setSending(false);
    }
  };

  // Send In-App Notification
  const handleSendInApp = async () => {
    if (!inAppTitle.trim() || !inAppBody.trim()) {
      toast.error('Please enter title and body');
      return;
    }

    setSending(true);
    try {
      await sendUserNotification(
        user.id,
        {
          type: inAppType,
          title: inAppTitle,
          body: inAppBody
        },
        adminId
      );
      toast.success('In-app notification sent');
      onSent?.();
      onClose();
    } catch (error: any) {
      console.error('Send in-app error:', error);
      toast.error(error.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const canSendEmail = !!user.email;
  const canSendSMS = !!user.phone;
  const canSendPush = !!user.fcmToken;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Bell className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-semibold">Send Notification</h2>
              <p className="text-purple-100 text-sm">
                To: {user.displayName || user.email || user.phone}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Type Tabs */}
        <div className="border-b px-4 py-2 flex gap-2 flex-shrink-0">
          <button
            onClick={() => setActiveType('push')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeType === 'push' 
                ? 'bg-purple-100 text-purple-700' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Smartphone className="h-4 w-4" />
            Push
            {!canSendPush && <Badge variant="outline" className="text-xs ml-1">No Token</Badge>}
          </button>
          <button
            onClick={() => setActiveType('in_app')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeType === 'in_app' 
                ? 'bg-purple-100 text-purple-700' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            In-App
          </button>
          <button
            onClick={() => setActiveType('email')}
            disabled={!canSendEmail}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeType === 'email' 
                ? 'bg-purple-100 text-purple-700' 
                : canSendEmail ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <Mail className="h-4 w-4" />
            Email
            {!canSendEmail && <Badge variant="outline" className="text-xs ml-1">No Email</Badge>}
          </button>
          <button
            onClick={() => setActiveType('sms')}
            disabled={!canSendSMS}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeType === 'sms' 
                ? 'bg-purple-100 text-purple-700' 
                : canSendSMS ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <Phone className="h-4 w-4" />
            SMS
            {!canSendSMS && <Badge variant="outline" className="text-xs ml-1">No Phone</Badge>}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Templates */}
          <div className="mb-6">
            <Label className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4" />
              Templates
            </Label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES[activeType]?.map((template: any) => (
                <button
                  key={template.id}
                  onClick={() => loadTemplate(template.id, activeType)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    selectedTemplate === template.id
                      ? 'bg-purple-100 border-purple-300 text-purple-700'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          {/* Email Form */}
          {activeType === 'email' && (
            <div className="space-y-4">
              <div>
                <Label>To</Label>
                <Input value={user.email || ''} disabled className="bg-gray-50" />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject..."
                />
              </div>
              <div>
                <Label>Body</Label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Email body..."
                  rows={10}
                  className="w-full border rounded-lg px-3 py-2 resize-none font-mono text-sm"
                />
              </div>
            </div>
          )}

          {/* SMS Form */}
          {activeType === 'sms' && (
            <div className="space-y-4">
              <div>
                <Label>To</Label>
                <Input value={user.phone || ''} disabled className="bg-gray-50" />
              </div>
              <div>
                <Label className="flex justify-between">
                  <span>Message</span>
                  <span className={`text-sm ${smsBody.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>
                    {smsBody.length}/160
                  </span>
                </Label>
                <textarea
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  placeholder="SMS message..."
                  rows={4}
                  maxLength={160}
                  className="w-full border rounded-lg px-3 py-2 resize-none"
                />
                {smsBody.length > 160 && (
                  <p className="text-red-500 text-xs mt-1">SMS messages must be 160 characters or less</p>
                )}
              </div>
            </div>
          )}

          {/* Push Notification Form */}
          {activeType === 'push' && (
            <div className="space-y-4">
              {!canSendPush && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">
                    This user has no FCM token registered. They may not have enabled push notifications or haven't opened the app recently.
                  </p>
                </div>
              )}
              <div>
                <Label>Title</Label>
                <Input
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  placeholder="Notification title..."
                  maxLength={65}
                />
                <p className="text-xs text-gray-400 mt-1">{pushTitle.length}/65</p>
              </div>
              <div>
                <Label>Body</Label>
                <textarea
                  value={pushBody}
                  onChange={(e) => setPushBody(e.target.value)}
                  placeholder="Notification body..."
                  rows={3}
                  maxLength={240}
                  className="w-full border rounded-lg px-3 py-2 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">{pushBody.length}/240</p>
              </div>
            </div>
          )}

          {/* In-App Notification Form */}
          {activeType === 'in_app' && (
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <div className="flex gap-2 mt-1">
                  {(['info', 'success', 'warning'] as const).map(type => (
                    <button
                      key={type}
                      onClick={() => setInAppType(type)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                        inAppType === type
                          ? type === 'info' ? 'bg-blue-100 text-blue-700'
                            : type === 'success' ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={inAppTitle}
                  onChange={(e) => setInAppTitle(e.target.value)}
                  placeholder="Notification title..."
                />
              </div>
              <div>
                <Label>Body</Label>
                <textarea
                  value={inAppBody}
                  onChange={(e) => setInAppBody(e.target.value)}
                  placeholder="Notification body..."
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 resize-none"
                />
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Preview:</p>
                <div className={`p-3 rounded-lg border ${
                  inAppType === 'info' ? 'bg-blue-50 border-blue-200'
                    : inAppType === 'success' ? 'bg-green-50 border-green-200'
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  <p className="font-medium text-sm">{inAppTitle || 'Title'}</p>
                  <p className="text-xs text-gray-600 mt-1">{inAppBody || 'Body text'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={
              activeType === 'email' ? handleSendEmail
                : activeType === 'sms' ? handleSendSMS
                : activeType === 'push' ? handleSendPush
                : handleSendInApp
            }
            disabled={sending || (activeType === 'email' && !canSendEmail) || (activeType === 'sms' && !canSendSMS)}
            className="bg-purple-500 hover:bg-purple-600"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send {activeType === 'in_app' ? 'In-App' : activeType.charAt(0).toUpperCase() + activeType.slice(1)}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
